import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { createFileRoute, Link } from '@tanstack/react-router'
import { PaginationState } from '@tanstack/react-table'
import { format, parse } from 'date-fns'
import { compact } from 'lodash-es'
import { ChevronDownIcon, EyeIcon, FilterIcon, XIcon } from 'lucide-react'
import { DateRange } from 'react-day-picker'
import { z } from 'zod'

import { cn } from '@/lib/utils'
import { useDexieQuery } from '@/hooks/use-dexie-query'
import { useIsMobile } from '@/hooks/use-mobile.ts'
import { usePagination } from '@/hooks/use-pagination'
import { Button } from '@/components/ui/button'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Breadcrumbs } from '@/components/breadcrumbs'
import {
  DataTable,
  DataTableColumnDef,
} from '@/components/data-table/data-table'
import { DateFormat } from '@/components/date-format'
import { DateRangePicker } from '@/components/date-range-picker'
import { PageTitle } from '@/components/layout/page-title'
import type { DexieDatabase, InterventoDoc } from '@/db/local/dexie-db'
import { useMyRole } from '@/features/auth/hooks/use-my-role'
import { InterventoStatoBadge } from '@/features/interventi/components/stato-badge'
import { StatoFilterSelect } from '@/features/interventi/components/stato-filter-select'
import {
  applyInterventoFilters,
  type Filterable,
} from '@/features/interventi/utils/apply-intervento-filters'
import { legacyStatoFilter } from '@/features/interventi/utils/stato'
import { useWho } from '@/features/riparazioni/use-who'

export const Route = createFileRoute('/_authenticated/interventi/')({
  component: Interventi,
  ssr: false,
  validateSearch: z
    .object({
      ricerca: z.coerce.string().optional(),
      pagina: z.coerce.number().optional(),
      perPagina: z.coerce.number().optional(),
      dataDa: z.coerce.string().optional(),
      dataA: z.coerce.string().optional(),
      stato: z.coerce.string().optional(),
      // Old links (bookmarks): read as the stato they meant, then replaced
      status: z.coerce.string().optional(),
      esito: z.coerce.string().optional(),
      operatoreId: z.coerce.string().optional(),
    })
    .optional(),
})

const ConditionalLink: React.FC<{
  children?: React.ReactNode
  interventoId: string
  role?: string | null
}> = ({ children, interventoId, role }) => {
  const isMobile = useIsMobile()

  // Determine the appropriate route based on role
  const route =
    role === 'committente'
      ? '/interventi/visualizza/$interventoId'
      : '/interventi/modifica/$interventoId'

  return isMobile ? (
    <Link to={route} params={{ interventoId }}>
      {children}
    </Link>
  ) : (
    <>{children}</>
  )
}

function Interventi() {
  const searchParams = Route.useSearch()
  const {
    pagina = 1,
    perPagina = 20,
    ricerca = '',
    dataDa,
    dataA,
    status,
    esito,
    operatoreId,
  } = searchParams ?? {}
  const stato = searchParams?.stato ?? legacyStatoFilter(status, esito)
  const navigate = Route.useNavigate()
  const isMobile = useIsMobile()
  const [filtersExpanded, setFiltersExpanded] = useState(false)
  const myRole = useMyRole()

  // An old link's ?status= and ?esito= become the ?stato= they meant, in place:
  // left in the address they would come back whenever the stato is cleared
  useEffect(() => {
    if (status === undefined && esito === undefined) return
    navigate({
      search: (s) => ({
        ...s,
        stato: s?.stato ?? legacyStatoFilter(s?.status, s?.esito),
        status: undefined,
        esito: undefined,
      }),
      replace: true,
    })
  }, [status, esito, navigate])

  const parseDate = (value?: string) =>
    value ? parse(value, 'yyyy-MM-dd', new Date()) : undefined
  const dateRange: DateRange | undefined = {
    from: parseDate(dataDa),
    to: parseDate(dataA),
  }

  const { skip, limit } = usePagination(pagina as number, perPagina as number)

  // Helper function to apply filters to a query
  // An operatore's list: their own interventi, not those looked up for a
  // repair and never started (admins and committenti see what they pull)
  const who = useWho()
  const mine = who && !who.admin ? who.userId : undefined

  // Chained, never awaited in an async function: the live query follows the
  // queries made in a Dexie promise's then(), not after a native await
  const applyFilters = useCallback(
    <T extends Filterable<T>>(db: DexieDatabase, query: T) =>
      applyInterventoFilters(db, query, {
        ricerca,
        dateRange,
        stato,
        operatoreId,
        mine,
      }),
    [ricerca, dateRange, stato, operatoreId, mine]
  )

  const interventi = useDexieQuery(
    (db) =>
      applyFilters(db, db.interventi.orderBy('data').reverse()).then((query) =>
        query.offset(skip).limit(limit).toArray()
      ),
    [skip, limit, ricerca, dataDa, dataA, stato, operatoreId, mine]
  )

  const interventiCount = useDexieQuery(
    (db) =>
      applyFilters(db, db.interventi.toCollection()).then((query) =>
        query.count()
      ),
    [ricerca, dataDa, dataA, stato, operatoreId, mine]
  )

  const filtersActive = Boolean(
    ricerca || dataDa || dataA || stato || operatoreId
  )

  // Add a new query to get all utenti for operator lookup
  const utenti = useDexieQuery(
    (db) => db.utenti.orderBy('username').toArray(),
    []
  )

  // Create a map for quick operator lookup
  const operatoreMap = useMemo(() => {
    if (!utenti) return new Map()
    return new Map(utenti.map((utente) => [utente.user_id, utente]))
  }, [utenti])

  const columns: Array<DataTableColumnDef<InterventoDoc>> = useMemo(
    () =>
      compact([
        {
          accessorKey: 'id_interno',
          header: 'ID',
          cell: ({ row, cell }) => (
            <ConditionalLink interventoId={row.original.id} role={myRole}>
              {cell.getValue() as React.ReactNode}
            </ConditionalLink>
          ),
        },
        {
          id: 'stato',
          header: 'Stato',
          cell: ({ row }) => (
            <ConditionalLink interventoId={row.original.id} role={myRole}>
              <InterventoStatoBadge
                intervento={row.original}
                iconOnly={isMobile}
              />
            </ConditionalLink>
          ),
        },
        {
          accessorKey: 'data',
          header: 'Data',
          cell: ({ row }) => (
            <ConditionalLink interventoId={row.original.id} role={myRole}>
              <DateFormat date={row.original.data} format="DD/MM/YYYY HH:mm" />
            </ConditionalLink>
          ),
        },
        // Only show operatore column for non-committente users
        myRole !== 'committente' && {
          accessorKey: 'operatore_id',
          header: 'Operatore',
          // The inspector, and the operatore of the latest repair when another
          cell: ({ row }) => {
            const { operatore_id, current_operatore_id } = row.original
            const name = (id?: string | null) =>
              operatoreMap.get(id || '')?.username || '-'
            return (
              <ConditionalLink interventoId={row.original.id} role={myRole}>
                {name(operatore_id)}
                {current_operatore_id &&
                  current_operatore_id !== operatore_id && (
                    <span className="text-muted-foreground block text-xs">
                      Riparazione: {name(current_operatore_id)}
                    </span>
                  )}
              </ConditionalLink>
            )
          },
        },
        {
          accessorKey: 'indirizzo',
          header: 'Indirizzo',
          className: 'md:whitespace-normal md:break-words md:max-w-xs',
          cell: ({ row, cell }) => (
            <ConditionalLink interventoId={row.original.id} role={myRole}>
              {cell.getValue() as React.ReactNode}
            </ConditionalLink>
          ),
        },
        {
          accessorKey: 'caditoie_num',
          header: 'Numero Caditoie',
          cell: ({ row, cell }) => (
            <ConditionalLink interventoId={row.original.id} role={myRole}>
              {cell.getValue() as React.ReactNode}
            </ConditionalLink>
          ),
        },
        !isMobile && {
          id: 'actions',
          cell: ({ row }) => {
            const route =
              myRole === 'committente'
                ? '/interventi/visualizza/$interventoId'
                : '/interventi/modifica/$interventoId'

            return (
              <div className="flex items-center justify-end">
                <Button className="h-auto" variant="ghost" size="sm" asChild>
                  <Link to={route} params={{ interventoId: row.original.id }}>
                    <EyeIcon className="size-4" />
                    <span className="hidden md:flex">Visualizza</span>
                  </Link>
                </Button>
              </div>
            )
          },
        },
      ]),
    [isMobile, operatoreMap, myRole]
  )

  const handlePaginationChange = (pagination: PaginationState) => {
    navigate({
      search: (s) => ({
        ...s,
        pagina: pagination.pageIndex + 1,
        perPagina: pagination.pageSize,
      }),
      resetScroll: false,
    })
  }

  const handleDateRangeChange = useCallback(
    (range: DateRange | undefined) => {
      const normalizedRange = range?.from
        ? { from: range.from, to: range.to ?? range.from }
        : undefined

      navigate({
        search: (s) => ({
          ...s,
          dataDa: normalizedRange?.from
            ? formatDateParam(normalizedRange.from)
            : undefined,
          dataA: normalizedRange?.to
            ? formatDateParam(normalizedRange.to)
            : undefined,
          pagina: 1,
        }),
      })
    },
    [navigate]
  )

  const handleClearFilters = () => {
    navigate({
      search: (s) => ({
        ...s,
        ricerca: undefined,
        dataDa: undefined,
        dataA: undefined,
        stato: undefined,
        status: undefined,
        esito: undefined,
        operatoreId: undefined,
        pagina: 1,
      }),
    })
  }

  return (
    <>
      <Breadcrumbs items={[{ name: 'Interventi', to: '/interventi' }]} />

      <PageTitle className="flex items-center justify-between">
        <span>Interventi</span>
      </PageTitle>

      <div className="py-4">
        <Collapsible
          open={isMobile ? filtersExpanded || filtersActive : true}
          onOpenChange={isMobile ? setFiltersExpanded : undefined}
        >
          {/* Mobile filters toggle */}
          {isMobile && (
            <CollapsibleTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="w-full justify-between"
              >
                <div className="flex items-center gap-2">
                  <FilterIcon className="size-4" />
                  <span>Filtri</span>
                  {filtersActive && (
                    <span className="bg-primary text-primary-foreground rounded-full px-2 py-0.5 text-xs">
                      {
                        [ricerca, dataDa, dataA, stato, operatoreId].filter(
                          Boolean
                        ).length
                      }
                    </span>
                  )}
                </div>
                <ChevronDownIcon
                  className={`size-4 transition-transform ${filtersExpanded || filtersActive ? 'rotate-180' : ''}`}
                />
              </Button>
            </CollapsibleTrigger>
          )}

          {/* Filters container */}
          <CollapsibleContent className={cn('mt-2', isMobile ? '' : 'block')}>
            <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:gap-1.5">
                <Input
                  placeholder="Cerca..."
                  value={ricerca}
                  onChange={(e) =>
                    navigate({
                      search: (s) => ({
                        ...s,
                        ricerca: e.target.value || undefined,
                        pagina: 1,
                      }),
                    })
                  }
                  className="w-full lg:w-[220px]"
                />

                <StatoFilterSelect
                  className="w-full lg:w-[210px]"
                  value={stato}
                  onChange={(value) =>
                    navigate({
                      search: (s) => ({
                        ...s,
                        stato: value,
                        status: undefined,
                        esito: undefined,
                        pagina: 1,
                      }),
                    })
                  }
                />

                {myRole === 'admin' && (
                  <Select
                    value={operatoreId || 'all'}
                    onValueChange={(value) =>
                      navigate({
                        search: (s) => ({
                          ...s,
                          operatoreId: value === 'all' ? undefined : value,
                          pagina: 1,
                        }),
                      })
                    }
                  >
                    <SelectTrigger className="w-full lg:w-[180px]">
                      <SelectValue placeholder="Tutti gli operatori" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tutti gli operatori</SelectItem>
                      {utenti?.map((utente) => (
                        <SelectItem key={utente.user_id} value={utente.user_id}>
                          {utente.username}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}

                <DateRangePicker
                  className="w-full lg:w-auto"
                  placeholder={
                    <span className="text-muted-foreground">Data</span>
                  }
                  date={dateRange}
                  onChange={handleDateRangeChange}
                  disableFutureDates={true}
                />

                {filtersActive && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleClearFilters}
                    className="w-full lg:w-auto lg:whitespace-nowrap"
                  >
                    <XIcon className="size-4" />
                    <span className="ml-1">Cancella filtri</span>
                  </Button>
                )}
              </div>
            </div>
          </CollapsibleContent>
        </Collapsible>
        <DataTable
          columns={columns}
          data={interventi ?? []}
          getRowId={(row) => row.id}
          count={interventiCount}
          initialPage={pagina as number}
          pageSize={perPagina as number}
          onPaginationChange={handlePaginationChange}
          showTotalCount={true}
        />
      </div>
    </>
  )
}

function formatDateParam(date: Date) {
  return format(date, 'yyyy-MM-dd')
}
